import { useCallback, useRef } from "react";
import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import type {
  FeatureDiscoveryData,
  FeatureDiscoveryKey
} from "@/lib/types";

interface MarkFeatureDiscoveredOptions {
  source: string;
  metadata?: Record<string, unknown>;
}

const EMPTY_DISCOVERIES: Partial<
  Record<FeatureDiscoveryKey, FeatureDiscoveryData>
> = {};

export function useFeatureDiscovery() {
  const { api } = useAppContext();
  const {
    userData,
    setFeatureDiscoveryOnUserData
  } = useAuthContext();
  const inFlightRef = useRef<Set<FeatureDiscoveryKey>>(new Set());

  const discoveries = userData?.feature_discoveries ?? EMPTY_DISCOVERIES;

  const isDiscovered = useCallback(
    (featureKey: FeatureDiscoveryKey) => Boolean(discoveries[featureKey]),
    [discoveries]
  );

  const markDiscovered = useCallback(
    async (
      featureKey: FeatureDiscoveryKey,
      { source, metadata = {} }: MarkFeatureDiscoveredOptions
    ) => {
      if (!userData) return;
      if (discoveries[featureKey]) return;
      if (inFlightRef.current.has(featureKey)) return;

      inFlightRef.current.add(featureKey);
      try {
        const persisted = await api.markFeatureDiscovered(featureKey, {
          source,
          metadata
        });
        setFeatureDiscoveryOnUserData(featureKey, persisted);
      } catch (error) {
        console.error("Failed to mark feature discovered:", error);
      } finally {
        inFlightRef.current.delete(featureKey);
      }
    },
    [api, discoveries, setFeatureDiscoveryOnUserData, userData]
  );

  return {
    discoveries,
    isDiscovered,
    markDiscovered
  };
}
